import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Settings, LogOut, User, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { NotificationBell } from './NotificationBell';
import { TwoFactorDialog } from './two-factor/TwoFactorDialog';
import { SessionTimer } from './SessionTimer';
import { apiService } from '../services/api';
import { parseGotoInput, buildPortalTargetUrl, getCustomerPortalBaseUrl } from '../utils/portalLink';
import { resolveGotoTarget } from '../utils/gotoResolver';
import { extractErrorMessage } from '../utils/errorHandler';
import { TenantSelector } from './TenantSelector';

interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  isAuthenticated: boolean;
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

interface HeaderProps {
  user: User;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
}

export function Header({ user, onLogout, onNavigateToProfile }: HeaderProps) {
  const [portalLink, setPortalLink] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);

  const getInitials = (user: User) => {
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  /**
   * Löst die Eingabe auf den zugehörigen Customer auf, startet die Impersonation
   * und öffnet das Ziel direkt in einem neuen Tab.
   *
   * Akzeptiert eine Rufnummer (springt auf die Detailseite des zugeordneten Agents),
   * jeden Agent-Link inklusive Unterseiten (Reporting, Versionen, Test, QA, Teststudio,
   * einzelne Conversation …) sowie Knowledge-Links. Query und Fragment des eingefügten
   * Links bleiben erhalten, sodass auch tiefe Links unverändert am Ziel ankommen.
   */
  const handleGoToPortal = async () => {
    if (isResolving) return;

    const target = parseGotoInput(portalLink);
    if (!target) {
      toast.error('Ungültige Eingabe', {
        description:
          'Erwartet wird eine Rufnummer oder ein Customer-Portal-Link, z.B. …/voicebots/<id>/reporting, ' +
          '…/chatbots/<id>/conversations/<id> oder …/knowledge/<id>.',
        duration: 5000,
      });
      return;
    }

    setIsResolving(true);
    try {
      // 1. Ziel auflösen → zugehörigen Customer und finalen Pfad ermitteln.
      const resolved = await resolveGotoTarget(target, apiService);

      // 2. Impersonation-Token für diesen Customer erzeugen.
      const impersonation = await apiService.startImpersonation(
        resolved.customerId,
        `Admin goto impersonation for ${resolved.kindLabel} ${resolved.name}`,
      );

      // 3. Direkt ans Ziel navigieren — der impersonation_token greift auf jeder Route.
      //    Bei einem Link den Host der Eingabe bevorzugen (z.B. localhost beim lokalen
      //    Testen), bei einer Rufnummer gibt es keinen — dann die konfigurierte Basis-URL.
      const linkLocation = target.kind === 'phone-number' ? undefined : target;
      const baseUrl = linkLocation?.origin ?? getCustomerPortalBaseUrl();
      const targetUrl = buildPortalTargetUrl(baseUrl, resolved.path, impersonation.token, linkLocation);

      window.open(targetUrl, '_blank', 'noopener,noreferrer');

      // Beim Rufnummern-Weg die getroffene Nummer mit anzeigen — der Admin sieht so,
      // WELCHE gespeicherte Nummer der Lookup getroffen hat, bevor er weiterarbeitet.
      toast.success(`${resolved.kindLabel} geöffnet`, {
        description: resolved.matchedPhoneNumber
          ? `Rufnummer ${resolved.matchedPhoneNumber} → ${resolved.name}. Impersonation gestartet — in neuem Tab geöffnet.`
          : `Impersonation für ${resolved.name} gestartet — in neuem Tab geöffnet.`,
        duration: 4000,
      });
      setPortalLink('');
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'Ziel konnte nicht geöffnet werden.');
      toast.error('Fehler beim Öffnen', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-4 shrink-0">
          <h2 className="text-xl font-semibold text-gray-800">
            Administration
          </h2>
          <TenantSelector />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); void handleGoToPortal(); }}
          className="flex items-center gap-2 flex-1 max-w-xl"
        >
          <Input
            type="text"
            value={portalLink}
            onChange={(e) => { setPortalLink(e.target.value); }}
            placeholder="Rufnummer, Agent-, Chat- oder Knowledge-Link einfügen…"
            disabled={isResolving}
            aria-label="Rufnummer oder Customer-Portal-Link zum direkten Öffnen via Impersonation"
            className="flex-1"
          />
          <Button type="submit" disabled={isResolving || !portalLink.trim()} className="shrink-0">
            {isResolving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            Go to
          </Button>
        </form>

        <div className="flex items-center space-x-4 shrink-0">
          {/* Nur Super-Admins: die Error-Events-Summary-API ist Super-Admin-only. */}
          {user.role === 'super_admin' && <NotificationBell />}

          <SessionTimer />

          {/* modal={false}: sonst blockiert das Menü beim Öffnen des Dialogs Fokus/Pointer-Events. */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-100 text-blue-700">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user.name ?? 'Administrator'}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onNavigateToProfile}>
                <User className="mr-2 h-4 w-4" />
                <span>Mein Profil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTwoFactorOpen(true)}>
                <ShieldCheck className="mr-2 h-4 w-4" />
                <span>Zwei-Faktor-Authentifizierung</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Einstellungen</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Abmelden</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <TwoFactorDialog open={twoFactorOpen} onOpenChange={setTwoFactorOpen} />
        </div>
      </div>
    </header>
  );
}
